import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PortalShell } from "@/components/layout/PortalShell";
import { AGENT_NAV } from "@/lib/agent-nav";
import { PortalPage } from "@/components/portal/PortalPage";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Seal } from "@/components/brand/Seal";

interface ApplicationRow {
  id: string;
  full_name: string;
  email: string;
  state: string | null;
  status: string;
  heard_about: string | null;
  created_at: string;
}

export const Route = createFileRoute("/agent/referrals")({
  head: () => ({ meta: [{ title: "Referrals — Strategist Portal" }, { name: "robots", content: "noindex" }] }),
  component: () => (
    <ProtectedRoute requiredRole="agent">
      <PortalShell title="Strategist Portal" nav={AGENT_NAV} tourKey="agent_tour">
        <AgentReferrals />
      </PortalShell>
    </ProtectedRoute>
  ),
});

function AgentReferrals() {
  const { user, profile } = useAuth();
  const [rows, setRows] = useState<ApplicationRow[]>([]);

  useEffect(() => {
    if (!user) return;
    void loadReferrals();
  }, [user?.id]);

  async function loadReferrals() {
    const { data } = await supabase
      .from("advisor_applications")
      .select("id, full_name, email, state, status, heard_about, created_at")
      .order("created_at", { ascending: false });
    setRows(data ?? []);
  }

  const filtered = useMemo(() => {
    const tokens = [profile?.full_name, profile?.email, user?.email].filter(Boolean).map((value) => String(value).toLowerCase());
    if (tokens.length === 0) return rows;
    return rows.filter((row) => tokens.some((token) => (row.heard_about ?? "").toLowerCase().includes(token)));
  }, [profile?.email, profile?.full_name, rows, user?.email]);

  return (
    <PortalPage eyebrow="Growth" title="Referrals" intro="Strategist candidate applications attributed to your name or email in the referral source field.">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Seal title={String(filtered.length)} subtitle="Attributed" size="sm" />
        <Seal title={String(filtered.filter((row) => row.status === "approved").length)} subtitle="Approved" size="sm" />
        <Seal title={String(filtered.filter((row) => row.status !== "approved" && row.status !== "rejected").length)} subtitle="In review" size="sm" />
      </div>
      <div className="bg-white border border-bronze-600/20 rounded-[4px] overflow-x-auto shadow-soft">
        <table className="w-full text-[13px]">
          <thead className="text-left text-charcoal-500 font-sans uppercase tracking-[0.12em] text-[10px]"><tr className="border-b border-bronze-600/20"><th className="px-4 py-3">Candidate</th><th className="px-4 py-3">State</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Source</th><th className="px-4 py-3">Applied</th></tr></thead>
          <tbody>
            {filtered.length === 0 ? <tr><td colSpan={5} className="px-4 py-10 text-center font-sans text-[13px] text-charcoal-500">No attributed referrals found.</td></tr> : filtered.map((row) => (
              <tr key={row.id} className="border-b border-bronze-600/10"><td className="px-4 py-3 font-medium text-navy-800">{row.full_name}<br /><span className="font-normal text-charcoal-500">{row.email}</span></td><td className="px-4 py-3">{row.state ?? "—"}</td><td className="px-4 py-3 capitalize">{row.status}</td><td className="px-4 py-3 text-charcoal-500">{row.heard_about ?? "—"}</td><td className="px-4 py-3 text-charcoal-500">{new Date(row.created_at).toLocaleDateString()}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </PortalPage>
  );
}